import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const glitchSliceTextSkill: Skill = {
    name: SKILL_NAMES.glitchSliceText,
    kind: 'codeo',
    description:
        'Tear a word into offset colour-separated slices for a few frames, then snap it clean — ' +
        'for a break, a disruption, a warning, or a moment that should feel unstable.',
    instructions: `# Glitch Slice Text

A word splits into horizontal bands that jump sideways, its colour channels separate, and then it snaps back into register. It fires for a few frames, twice at most, and resolves. It reads as instability — a system under strain, a disruption, something breaking — and it is the wrong choice for anything that should feel trustworthy.

Every offset must come from the timeline, never a random draw, or the glitch re-rolls on every rendered frame and becomes permanent noise.

## Markup

Three stacked copies of the word: two channel ghosts and the clean original on top.

\`\`\`html
<div id="glitch" style="position: absolute; left: 0; right: 0; top: 440px; text-align: center; font-family: var(--l5-font-0), sans-serif; font-size: 190px; font-weight: 900; letter-spacing: -0.02em; line-height: 1;">
  <div id="glitch-r" style="position: absolute; left: 0; right: 0; color: var(--l5-color-2, #ff3b5c); opacity: 0;">BROKEN</div>
  <div id="glitch-c" style="position: absolute; left: 0; right: 0; color: var(--l5-color-3, #22d3ee); opacity: 0;">BROKEN</div>
  <div id="glitch-main" style="position: relative; color: var(--l5-color-0, #f4f6f9);">BROKEN</div>
</div>
\`\`\`

## Timeline code

Two short bursts with a clean gap between them. \`steps(1)\` makes each offset snap rather than slide.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const BURST = 1.2;
// Fixed offsets, so the same frame always renders the same tear.
const SLICES = [
    { x: -18, clip: 'inset(18% 0 62% 0)' },
    { x: 22, clip: 'inset(52% 0 26% 0)' },
    { x: -9, clip: 'inset(72% 0 8% 0)' },
];

SLICES.forEach((slice, index) => {
    tl.to('#glitch-main', { x: slice.x, clipPath: slice.clip, duration: 0.06, ease: 'steps(1)' }, BURST + index * 0.06);
});
tl.to('#glitch-main', { x: 0, clipPath: 'inset(0% 0 0% 0)', duration: 0.06, ease: 'steps(1)' }, BURST + 0.18);

tl.to('#glitch-r', { opacity: 0.85, x: -14, duration: 0.06, ease: 'steps(1)' }, BURST);
tl.to('#glitch-c', { opacity: 0.85, x: 14, duration: 0.06, ease: 'steps(1)' }, BURST);
tl.to('#glitch-r, #glitch-c', { opacity: 0, x: 0, duration: 0.06, ease: 'steps(1)' }, BURST + 0.18);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Burst length**: 0.12–0.24s. Longer and it stops reading as a fault and starts reading as a style.
- **Two bursts**: a second, shorter burst 0.4s after the first is markedly more convincing than one — real faults recur. Never a third.
- **Channel separation**: 10–18px at this size. The two ghost colours should be complementary, and both should come from the palette.
- **Scan band**: a thin translucent bar crossing the word during the burst adds a CRT register — see \`${SKILL_NAMES.lightSweepPass}\` for the band handling.
- **Resolve into clean**: the snap back to register is the payoff. A glitch that never resolves leaves the frame looking broken rather than dramatic.
- **On the word**: with narration, the burst fires on the spoken word, not near it.

## Pitfalls

- Never use \`Math.random()\` for slice offsets. Frames render out of order and in fresh contexts, so a random tear differs every frame and the word becomes unreadable noise.
- The copies are \`x\`-tweened, so none may carry a CSS \`transform\`; centre them with \`left: 0; right: 0\` and \`text-align\`.
- All three copies must carry identical text and metrics, or the ghosts misalign when they return to register.
- The word is unreadable during the burst. Keep it short and let it sit clean before and after.
- Don't glitch a brand name or a product name. Instability is the message, and applying it to your own identity says the wrong thing.
- One glitched element per video. It is a strong stylistic claim and it wears out immediately.
- \`ease: 'steps(1)'\` is what makes each offset snap; a smooth ease turns the tear into a wobble.`,
};
